"use client";

import Flex from "@/components/core/flex";
import { HoverButton } from "@/components/shared/hoverButton";
import { useInfo } from "@/components/shared/infoProvider";
import SectionTitle from "@/components/shared/sectionTitle";
import Text from "@/components/shared/text";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import useLocalizer from "@/hooks/useLocalizer";
import { Link } from "@/i18n/routing";
import { cn, getFilePath } from "@/lib/utils/utils";
import { ProjectSection } from "@/types/home";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";
interface Props {
  data?: ProjectSection;
  className?: string;
}
export default function ProjectContent({ data, className }: Props) {
  const { t, isRtl } = useLocalizer();
  const projectSection = data;
  const clientInfo = useInfo();
  const projects = useMemo(() => {
    return data?.projects?.map((project) => {
      return {
        id: project.id,
        title: isRtl ? project.arTitle : project.enTitle,
        description:
          (isRtl ? project.arDescription : project.enDescription) ?? "",
        imageUrl: getFilePath(project?.image?.key ?? ""),
        href: `/projects/${project.id}`,
      };
    });
  }, [data, isRtl]);

  return (
    <section
      style={{
        backgroundImage: data?.banner
          ? `url(${getFilePath(data?.banner?.key ?? "")})`
          : undefined,
      }}
      className={cn(
        "relative w-full pt-28 pb-36 min-h-[659px] flex bg-cover bg-center bg-fixed bg-no-repeat bg-blend-overlay items-center",
        data?.banner && "bg-secondary/80",
        className,
      )}
    >
      <div className="relative container mx-auto px-6 block">
        <SectionTitle
          className="flex items-center justify-center mb-30"
          title={
            isRtl
              ? (projectSection?.arTitle ?? "")
              : (projectSection?.enTitle ?? "")
          }
          titleTag="h2"
          description={
            isRtl
              ? projectSection?.arDescription
              : projectSection?.enDescription
          }
          descriptionClassName="lg:text-3xl text-center text-white"
        />
        {projects && projects?.length > 0 && (
          <ProjectContentCarousel items={projects} />
        )}
        {clientInfo?.data?.info?.mobileNumber && (
          <Flex
            center
            flexWrap="wrap"
            className="mx-auto w-full max-w-2xl min-h-16 gap-2 p-2 border-2 border-white mt-10 rounded-full "
          >
            <Text className="text-white lg:text-lg lg:font-bold">
              {t("paragraphs.readyToServeYou")}
            </Text>
            <Link
              href={`tel:${clientInfo?.data?.info?.mobileNumber}`}
              aria-label={t("titles.callRightNow")}
              className="text-primary hover:text-white font-bold lg:text-lg"
            >
              {t("titles.callRightNow")}
            </Link>
          </Flex>
        )}
      </div>
    </section>
  );
}

interface ProjectContentCarouselProps {
  items: {
    id: number;
    title: string;
    description?: string;
    imageUrl: string;
    href?: string;
  }[];
}
const ProjectContentCarousel = ({ items }: ProjectContentCarouselProps) => {
  const { isRtl, t } = useLocalizer();
  const [count, setCount] = useState<number>(0);
  const [current, setCurrent] = useState<number>(0);
  const [api, setApi] = useState<CarouselApi>();
  const [api2, setApi2] = useState<CarouselApi>();
  const [next, setNext] = useState<"next" | "prev">("next");
  const [prev, setPrev] = useState<"next" | "prev">("prev");

  // const canScrollNext = useMemo(() => {
  //   return current != count;
  // }, [current, count]);

  // const canScrollPrev = useMemo(() => {
  //   return current != 1;
  // }, [current]);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList()?.length);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  useEffect(() => {
    if (!api2) {
      return;
    }

    api2.on("select", () => {
      setCurrent(api2.selectedScrollSnap());
    });
  }, [api2]);

  useEffect(() => {
    if (!api) {
      return;
    }
    if (isRtl) {
      setNext("prev");
      setPrev("next");
    } else {
      setNext("next");
      setPrev("prev");
    }
  }, [isRtl]);

  return (
    <Flex direction="col" className="relative xl:ml-30">
      <Carousel setApi={setApi}>
        <CarouselContent dir="ltr">
          {items?.map((item) => (
            <CarouselItem key={item.id}>
              <div className="relative  flex flex-col  w-full h-full">
                <div className="relative xl:w-[870px] w-full h-[612px]">
                  <Image
                    src={item.imageUrl}
                    alt={item?.title}
                    fill
                    priority
                    placeholder="empty"
                    className="object-cover absolute"
                  />
                </div>
                <div className="xl:absolute relative flex flex-col items-center xl:right-[calc(50%-380px)] xl:top-15 xl:w-[570px] w-full xl:h-full">
                  <div className="p-8 flex flex-col justify-between gap-4 w-full bg-white border border-border/50  min-h-[calc(100%-50%)]">
                    <div
                      dir={isRtl ? "rtl" : "ltr"}
                      className="flex flex-col justify-start gap-4"
                    >
                      <Text
                        as="h3"
                        aria-label={item?.title}
                        className="text-secondary font-bold line-clamp-2 "
                      >
                        {item.title}
                      </Text>
                      <Text className="text-secondary-foreground line-clamp-3">
                        {item.description}
                      </Text>
                    </div>
                    {item?.href && (
                      <div dir={isRtl ? "rtl" : "ltr"}>
                        <Link
                          href={item.href ?? "#"}
                          aria-label={`${t("buttons.discoverMore")}: ${item.title}`}
                        >
                          <HoverButton
                            variant="secondary"
                            className="h-14 text-white"
                          >
                            {t("buttons.discoverMore")}
                          </HoverButton>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="xl:absolute xl:bottom-25 xl:right-[calc(50%-380px)] xl:w-[570px] w-full my-5 xl:my-0">
          <Carousel
            setApi={setApi2}
            className="relative hover:[&_.proj-btns]:opacity-100"
          >
            <div>
              <CarouselContent dir="ltr" className="flex gap-2 z-30">
                {items?.map((item, index) => (
                  <CarouselItem
                    key={item.id}
                    className="lg:basis-1/4 sm:basis-1/3 basis-1/2 relative h-[130px] w-full z-20"
                    onClick={() => api?.scrollTo(index)}
                  >
                    {index !== current && (
                      <div className="absolute top-0 left-0 right-0 bottom-0 bg-black/20 z-10" />
                    )}
                    <Image
                      src={item.imageUrl}
                      alt={item?.title ?? ""}
                      fill
                      priority={index === 0}
                      placeholder="empty"
                      className="object-cover"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <HoverButton
                onClick={() => {
                  api?.scrollNext();
                  api2?.scrollNext();
                }}
                variant="secondary"
                aria-label={t("buttons.next")}
                className="h-14 w-14 proj-btns text-white z-30 absolute top-1/2 right-0 -translate-y-1/2  mx-12 transition-all duration-300 ease-in-out opacity-0 hover:opacity-100 "
              >
                {next == "next" ? (
                  <BsArrowRight size={20} aria-hidden="true" />
                ) : (
                  <BsArrowLeft size={20} aria-hidden="true" />
                )}
              </HoverButton>
              <HoverButton
                onClick={() => {
                  api?.scrollPrev();
                  api2?.scrollPrev();
                }}
                variant="secondary"
                aria-label={t("buttons.previous")}
                className="h-14 w-14 proj-btns text-white z-30 absolute top-1/2 left-0 -translate-y-1/2  mx-12 transition-all duration-300 ease-in-out opacity-0 hover:opacity-100 "
              >
                {prev == "prev" ? (
                  <BsArrowLeft size={20} aria-hidden="true" />
                ) : (
                  <BsArrowRight size={20} aria-hidden="true" />
                )}
              </HoverButton>
            </div>
          </Carousel>
        </div>
      </Carousel>
    </Flex>
  );
};
